import { Color } from '@/GlobalStyles';
import DateUtils from '@/utils/DateUtils';
import SvgUtils from '@/utils/pdfGenerator/SvgUtils';
import SvgChart, { SVGRenderer } from '@wuba/react-native-echarts/svgChart';
import { BarChart, LineChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  MarkAreaComponent,
  MarkLineComponent,
  TitleComponent,
  TooltipComponent,
  ToolboxComponent,
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutChangeEvent, Platform, View } from 'react-native';
import SimpleLink from '../SimpleLink';

// Register extensions
echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  SVGRenderer,
  BarChart,
  LineChart,
  MarkAreaComponent,
  LegendComponent,
  MarkLineComponent,
  ToolboxComponent,
]);

const E_HEIGHT = 400;
const E_WIDTH = 300;

type LogMarStatsChart = {
  data?: [number, number][];
};

const LogMarStatsChart = (props: LogMarStatsChart) => {
  const { data } = props;

  const chartRef = useRef<any>(null);
  const svgRef = useRef<any>(null);
  const [width, setWidth] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.resize({
        width: width,
        height: E_HEIGHT,
      });
    }
  }, [width]);

  useEffect(() => {
    let chart: any;
    if (svgRef.current) {
      const option = {
        animation: true,
        legend: {
          type: 'plain',
          bottom: 0,
        },
        tooltip: {
          trigger: 'axis',
          formatter: (value, index) => {
            const tooltip = value
              .map((element) => {
                return `${element.data[1]} -> ${element.data[0]} δ / ${element.data[0] < 0 ? `${(-1 / element.data[0]).toFixed(2)} m` : `${'Infini'}`}`;
              })
              .filter((e) => e !== null);

            return `${tooltip.join('<br/>')}`;
          },
          position: [0, 0],
        },
        grid: {
          left: '10%',
          right: '10%',
          bottom: '20%',
          top: '5%',
          containLabel: true,
        },
        xAxis: {
          type: 'value',
          axisLine: { onZero: false },
          max: 0,
          min: -4,
          inverse: true,
          axisLabel: {
            formatter: (value, index) => {
              return `${value} δ \n ${value < 0 ? `${(-1 / value).toFixed(2)} m` : `${'Infini'}`}`;
            },
          },
          interval: width < 600 ? 1 : 0.5,
        },
        yAxis: [
          {
            name: "Nombre d'occurrences",
            nameLocation: 'middle',
            nameTextStyle: {
              padding: [0, 0, 17, 0],
            },
            type: 'value',
          },
        ],
        series: [
          {
            unit: '',
            data: data,
            type: 'line',
            yAxisIndex: 0,
            showSymbol: false,
            smooth: true,
            color: Color.MainBleu,
            markArea: {
              data: [
                [
                  {
                    name: 'VL',

                    itemStyle: {
                      color: 'rgba(255, 173, 177, 0.4)',
                    },
                    xAxis: parseFloat((-1 / 1000).toFixed(2)),
                  },
                  {
                    xAxis: parseFloat((-1 / 3.5).toFixed(2)),
                  },
                ],
                [
                  {
                    name: 'VI',
                    itemStyle: {
                      color: 'rgba(116, 212, 175, 0.2)',
                    },
                    xAxis: parseFloat((-1 / 3.5).toFixed(2)),
                  },
                  {
                    xAxis: parseFloat((-1 / 0.5).toFixed(2)),
                  },
                ],
                [
                  {
                    name: 'VP',

                    itemStyle: {
                      color: 'rgba(79, 163, 189, 0.2)',
                    },
                    xAxis: parseFloat((-1 / 0.5).toFixed(2)),
                  },
                  {
                    xAxis: parseFloat((-1 / 0.00001).toFixed(2)),
                  },
                ],
              ],
            },
            areaStyle: { origin: 'start', opacity: 0.3 },
          },
        ],
      };

      // @ts-ignore
      if (!chartRef.current) {
        chart = echarts.init(svgRef.current, 'light', {
          renderer: 'svg',
          width: width,
          height: E_HEIGHT,
        });
        chartRef.current = chart;
      }

      chartRef.current.setOption(option);
    }
  }, [data, width]);

  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
    };
  }, []);

  const handleLayout = (e: LayoutChangeEvent) => {
    const { width } = e.nativeEvent.layout;
    setWidth(width);
  };

  return (
    <View onLayout={handleLayout} style={{ flex: 1, flexShrink: 0, flexBasis: 'auto' }}>
      <SvgChart ref={svgRef} />
      {Platform.OS === 'web' ? (
        <SimpleLink
          onPress={async () => {
            await SvgUtils.downloadPngFromSVG(
              chartRef.current?.getSvgDataURL(),
              width,
              Math.max(E_HEIGHT, width * 0.25),
              { x: 0, y: 0, width, height: Math.max(E_HEIGHT, width * 0.25), color: '#ffffff' },
              true,
              t('Repartition_Activite_Pref') + '_' + DateUtils.formatDate(new Date()) + '.png'
            );
          }}
        >
          Télécharger
        </SimpleLink>
      ) : null}
    </View>
  );
};

export default LogMarStatsChart;
